import Quill from 'quill'
import 'quill/dist/quill.snow.css'

import { useEffect, useRef } from 'react'

import { cn } from '@/lib/utils'

interface RichTextEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
}

const toolbarOptions = [
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  ['bold', 'italic', 'underline', 'strike', 'blockquote'],
  [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
  [{ align: [] }],
  [{ color: [] }, { background: [] }],
  ['link', 'image', 'video'],
  ['clean'],
  ['code-block'],
]

const RichTextEditor = ({ value, onChange, placeholder, className }: RichTextEditorProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const quillRef = useRef<Quill | null>(null)
  const onChangeRef = useRef(onChange)
  const isInternalChange = useRef(false)

  onChangeRef.current = onChange

  useEffect(() => {
    if (!containerRef.current) return

    const editorDiv = document.createElement('div')
    containerRef.current.appendChild(editorDiv)

    const quill = new Quill(editorDiv, {
      theme: 'snow',
      placeholder,
      modules: { toolbar: toolbarOptions },
    })

    quillRef.current = quill

    quill.on('text-change', () => {
      isInternalChange.current = true
      onChangeRef.current(quill.getSemanticHTML())
      isInternalChange.current = false
    })

    return () => {
      quillRef.current = null
      if (containerRef.current) {
        containerRef.current.innerHTML = ''
      }
    }
  }, [])

  // Sync external value changes into Quill without triggering onChange loop
  useEffect(() => {
    const quill = quillRef.current
    if (!quill || isInternalChange.current) return

    const current = quill.getSemanticHTML()
    if (current !== value) {
      const selection = quill.getSelection()
      quill.clipboard.dangerouslyPasteHTML(value ?? '')
      if (selection) quill.setSelection(selection)
    }
  }, [value])

  return (
    <div className={cn('rich-text-editor', className)}>
      <div ref={containerRef} className="bg-card" />
      <style>{`
        .ql-toolbar {
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
          border-color: hsl(var(--input)) !important;
          background-color: hsl(var(--subdued));
        }
        .ql-container {
          border-bottom-left-radius: 8px;
          border-bottom-right-radius: 8px;
          border-color: hsl(var(--input)) !important;
          font-family: inherit !important;
          font-size: 0.8125rem !important;
        }
        .ql-editor {
          min-height: 150px;
        }
        .ql-editor.ql-blank::before {
          color: hsl(var(--placeholder-foreground));
          font-style: normal;
        }
      `}</style>
    </div>
  )
}

export default RichTextEditor
